import React, { useState, useCallback, useEffect } from "react";
import useStyles from "./style";
import {
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  EditOutlined,
  DeleteOutline,
  AddOutlined,
  SearchOutlined,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";

// Services
import PackageDeliveryTypeApi from "Services/PackageDeliveryType";

// Modals
import PackageDeliveryTypeCreateModal from "Pages/Modals/PackageDeliveryType/Create";
import PackageDeliveryTypeUpdateModal from "Pages/Modals/PackageDeliveryType/Update";
import PackageDeliveryTypeDeleteDialog from "Pages/Modals/PackageDeliveryType/Delete";

const PackageDeliveryType = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const demoaccount_id = useSelector(
    (state) => state?.AuthReducer?.demoaccount_id || ""
  );

  const [row, setRow] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [searchText, setSearchText] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Modals
  const [addModal, setAddModal] = useState(false);
  const [updateModal, setUpdateModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const handleOpenAddModal = () => {
    setAddModal(true);
  };

  const handleCloseAddModal = () => {
    setAddModal(false);
    getPackageDeliveryTypes();
  };

  const handleOpenUpdateModal = (item) => {
    setSelectedId(item?._id);
    setUpdateModal(true);
  };

  const handleCloseUpdateModal = () => {
    setUpdateModal(false);
    getPackageDeliveryTypes();
  };

  const handleOpenDeleteModal = (item) => {
    setSelectedId(item?._id);
    setDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
    getPackageDeliveryTypes();
  };

  const getPackageDeliveryTypes = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setTotalPages(res?.data?.data?.totalRecords || 1);
        setRow(res?.data?.data?.data || []);
      } else {
        setRow([]);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch package delivery types",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    };
    const params = { page, limit: pageSize, search: searchText };
    if (demoaccount_id) {
      params.demoaccount_id = demoaccount_id;
    }
    PackageDeliveryTypeApi.getPackageDeliveryTypesList(params).then(onSuccess, onFailure);
  }, [page, pageSize, searchText, demoaccount_id, dispatch]);

  useEffect(() => {
    getPackageDeliveryTypes();
  }, [getPackageDeliveryTypes]);

  const calculateSno = (pageIndex, pSize, index) => {
    return (pageIndex - 1) * pSize + index + 1;
  };

  const dataRows = _.map(row || [], (data, index) => ({
    sno: calculateSno(page, pageSize, index),
    packageName: data?.packageName,
    packageImage: data?.packageImage,
    row: data,
  }));

  const totalPagesCount = Math.ceil(totalPages / pageSize) || 1;

  const renderPagination = () => {
    let pages = [];
    const internalPage = page - 1;
    if (totalPagesCount <= 5) {
      for (let i = 0; i < totalPagesCount; i++) pages.push(i);
    } else {
      if (internalPage < 3) {
        pages = [0, 1, 2, "...", totalPagesCount - 1];
      } else if (internalPage > totalPagesCount - 4) {
        pages = [0, "...", totalPagesCount - 3, totalPagesCount - 2, totalPagesCount - 1];
      } else {
        pages = [0, "...", internalPage - 1, internalPage, internalPage + 1, "...", totalPagesCount - 1];
      }
    }

    return pages.map((item, index) => {
      if (item === "...") {
        return (
          <span key={index} className={classes.pageDots}>
            ...
          </span>
        );
      }
      return (
        <Button
          key={item}
          className={internalPage === item ? classes.pageButtonActive : classes.pageButton}
          onClick={() => setPage(item + 1)}
        >
          {item + 1}
        </Button>
      );
    });
  };

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.headerContainer}>
        <Typography className={classes.pageTitle}>Package Delivery Types</Typography>
        <Typography className={classes.breadcrumb}>
          Admin / <span>Package Delivery Types</span>
        </Typography>
      </Box>

      {/* Action Row */}
      <Box className={classes.actionRow}>
        {/* Search */}
        <Box className={classes.searchWrapper}>
          <SearchOutlined className={classes.searchIcon} />
          <input
            type="text"
            placeholder="Search packages..."
            className={classes.searchInput}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(1);
            }}
          />
        </Box>

        {/* Right Actions */}
        <Box className={classes.rightActions}>
          {!demoaccount_id && (
            <Button
              variant="contained"
              className={classes.addButton}
              onClick={handleOpenAddModal}
              startIcon={<AddOutlined />}
            >
              Add New Package
            </Button>
          )}
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} className={classes.tableWrapper}>
        <Table>
          <TableHead>
            <TableRow className={classes.tableHeaderRow}>
              <TableCell className={classes.tableHeaderCell}>S.No</TableCell>
              <TableCell className={classes.tableHeaderCell}>Package Name</TableCell>
              <TableCell className={classes.tableHeaderCell}>Package Image</TableCell>
              {!demoaccount_id && (
                <TableCell className={classes.tableHeaderCell} align="right">Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {dataRows.map((rowItem) => (
              <TableRow key={rowItem.sno} className={classes.tableRow}>
                <TableCell className={classes.tableCell}>{rowItem.sno}</TableCell>
                <TableCell className={`${classes.tableCell} ${classes.boldText}`}>
                  {rowItem.packageName}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {rowItem.packageImage ? (
                    <Box
                      component="img"
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "8px",
                        objectFit: "cover",
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      }}
                      src={rowItem.packageImage}
                      alt={rowItem.packageName}
                    />
                  ) : (
                    "-"
                  )}
                </TableCell>
                {!demoaccount_id && (
                  <TableCell className={classes.tableCell} align="right">
                    <Box
                      component="span"
                      className={classes.actionButton}
                      onClick={() => handleOpenUpdateModal(rowItem.row)}
                    >
                      <EditOutlined fontSize="small" />
                      Edit
                    </Box>
                    <Box
                      component="span"
                      className={classes.actionDeleteButton}
                      onClick={() => handleOpenDeleteModal(rowItem.row)}
                    >
                      <DeleteOutline fontSize="small" />
                      Delete
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {dataRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={demoaccount_id ? 3 : 4} style={{ textAlign: "center", padding: "40px" }}>
                  <Typography variant="body2" color="textSecondary">
                    No package delivery types found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <Box className={classes.paginationContainer}>
          <Typography className={classes.paginationText}>
            Showing {(page - 1) * pageSize + (dataRows.length > 0 ? 1 : 0)} to{" "}
            {Math.min(page * pageSize, totalPages)} of {totalPages} results
          </Typography>

          <Box className={classes.paginationControls}>
            <ChevronLeft
              className={`${classes.pageArrow} ${page === 1 ? "disabled" : ""}`}
              onClick={() => page > 1 && setPage(page - 1)}
            />
            {renderPagination()}
            <ChevronRight
              className={`${classes.pageArrow} ${page === totalPagesCount ? "disabled" : ""}`}
              onClick={() => page < totalPagesCount && setPage(page + 1)}
            />
          </Box>
        </Box>
      </TableContainer>

      <PackageDeliveryTypeCreateModal
        openDialog={addModal}
        handleClose={handleCloseAddModal}
      />
      <PackageDeliveryTypeUpdateModal
        openDialog={updateModal}
        id={selectedId}
        handleClose={handleCloseUpdateModal}
      />
      <PackageDeliveryTypeDeleteDialog
        openDialog={deleteModal}
        id={selectedId}
        handleClose={handleCloseDeleteModal}
      />
    </Box>
  );
};

export default PackageDeliveryType;
